import { Component } from 'vue-property-decorator';
import { Location, Route } from 'vue-router';
import { email, required, sameAs } from 'vuelidate/lib/validators';

import { Popper } from '@monjin/vue-toolkit';

import { findInvitation } from '@api/claimInvitation';
import { checkEmailAvailability, createMember } from '@api/user';
import { matchError } from '@api/util';
import { NewMember } from '@model/candidate';
import { UserData } from '@model/invitation';

import PreviousRoute from '../../router/PreviousRoute';
import { ROUTE_REGISTRATION_CAND } from '../../router/routes';
import { hasNumber, hasSpecialChars, hasUppercase } from '../../util/index';

import FacebookLink from './Facebook.vue';
import GoogleLink from './Google.vue';
import LinkedinLink from './LinkedIn.vue';

import WithRender from './Registration.html';

interface PasswordHint {
    text: string;
    satisfied: boolean;
    type: string;
}

const PASSWORD_HINTS: PasswordHint[] = [
    {
        text: '8 - 20 characters',
        satisfied: false,
        type: 'lengthCheck'
    },
    {
        text: 'At least 1 uppercase character',
        satisfied: false,
        type: 'upperCaseCheck'
    },
    {
        text: 'At least 1 number',
        satisfied: false,
        type: 'numberCheck'
    },
    {
        text: 'At least 1 special character',
        satisfied: false,
        type: 'specialCharCheck'
    }
];
@WithRender
@Component({
    components: {
        FacebookLink, GoogleLink, LinkedinLink
    },
    validations: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        password: { required },
        password2: { sameAs: sameAs('password') }
    }
})
export default class Registration extends PreviousRoute {

    public $v: any;
    public $refs: {
        popper: Popper;
    };

    // Form model
    public firstName = '';
    public lastName = '';
    public email = '';
    public password = '';

    // One additional field
    public password2 = '';

    public isRequestInProgress = false;

    public passwordFieldType: string = 'password';
    public confirmPasswordFieldType: string = 'password';

    public checking = false;
    public emailTaken: boolean = false;
    public checkDone = false;
    public emailAvailabilityCheckFailed: boolean = false;
    public passwordCriterionSatisfied = true;
    public passwordHints: PasswordHint[] = PASSWORD_HINTS;
    public errorMessage: string = '';
    public isInviteCodeValid: boolean = false;

    public get isEmailDisabled(): boolean {
        return !!this.inviteCode && !!this.email;
    }

    public get userType() {
        return this.$route.name === ROUTE_REGISTRATION_CAND
            ? 'candidate' : 'interviewer';
    }

    public get inviteId() {
        // Default inviteId is 0. Zero means not invited by anybody.
        return (this.$route.query.inviteId || 0) as number;
    }

    public get source() {
        // Default source is 1.
        return (this.$route.query.source || 1) as number;
    }

    public get inviteCode(): string {
        return (this.$route.query.InviteCode || this.$route.query.inviteCode || '').toString();
    }

    public get socialUrlParams() {
        const query = this.$route.query as any;

        const params = new URLSearchParams({
            ...query,
            inviteCode: this.inviteCode,
            inviteId: this.inviteId,
            source: this.source,
            userType: this.userType
        });

        return params.toString();
    }

    public get googleLink() {
        return `/internal/auth/google?${this.socialUrlParams}`;
    }

    public get facebookLink() {
        return `/internal/auth/facebook?${this.socialUrlParams}`;
    }

    public get linkedinLink() {
        return `/internal/auth/linkedin?${this.socialUrlParams}`;
    }

    public beforeRouteEnter(to: Route, from: Route, next) {
        next((vm: this) => {
            if (to.query.errorMessage) {
                // Show error message to user sent from API and no need to keep it in URL.
                vm.errorMessage = vm.$route.query.errorMessage as string;
                vm.$snackbar.error({ message: vm.errorMessage });
            }

            vm.findInvitation(to.name!);
        });
    }

    public disablePaste(e) {
        e.preventDefault();
    }

    public hasError(model: string) {
        return this.$v[model].$dirty && this.$v[model].$error;
    }

    public onSubmit() {
        // Dirty all inputs
        this.$v.$touch();
        this.checkPasswordStrength();

        if (this.$v.$invalid) {
            return;
        }

        if (!this.passwordCriterionSatisfied) {
            return;
        }

        this.isRequestInProgress = true;

        const candidate: NewMember = {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
            userType: this.userType,
            source: this.source,
            inviteId: this.inviteId,
            inviteCode: this.inviteCode,
            additionalQueryParams: new URLSearchParams(this.$route.query as any).toString()
        };

        createMember(candidate)
            // User creation successful
            .then((_x) => window.location.href = '/@me/')
            .catch(this.apiError)
            .then(() => this.isRequestInProgress = false);
    }

    public switchVisiblity(fieldName: string) {
        if (fieldName === 'password') {
            this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
        } else {
            this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
        }
    }

    public checkAvailability() {

        if (this.email.trim() !== '') {

            this.$v.email.$touch();

            if (!this.$v.email.$invalid) {

                this.emailAvailabilityCheckFailed = false;
                this.checking = true;
                this.emailTaken = false;
                this.checkDone = false;

                checkEmailAvailability(this.email, this.userType, this.source)
                    .then((x: boolean) => {
                        this.emailAvailabilityCheckFailed = false;
                        this.emailTaken = x;
                    })
                    .catch((e) => {
                        this.emailAvailabilityCheckFailed = true;
                        this.$snackbar.error({ message: 'Checking email availibility failed'});
                    })
                    .then((x) => {
                        this.checking = false;
                        this.checkDone = true;
                    });
            }
        }
    }

    public checkPasswordStrength() {

        if (this.password.length === 0) {
            return;
        }

        this.passwordHints.map((x) => {
            x.satisfied = false;
        });

        if (this.password.length >= 8 && this.password.length <= 20) {
            this.setPasswordSatisfied('lengthCheck');
        }
        if (hasUppercase(this.password)) {
            this.setPasswordSatisfied('upperCaseCheck');
        }
        if (hasNumber(this.password)) {
            this.setPasswordSatisfied('numberCheck');
        }
        if (hasSpecialChars(this.password)) {
            this.setPasswordSatisfied('specialCharCheck');
        }

        this.passwordCriterionSatisfied = this.isPasswordCriterionSatisfied();

        if (!this.passwordCriterionSatisfied) {
            this.$refs.popper.show();
        }
    }

    private isPasswordCriterionSatisfied() {
        return this.passwordHints.every((x) => x.satisfied);
    }

    private setPasswordSatisfied(type: string) {
        this.passwordHints.map((x) => {
            if (x.type === type) {
                x.satisfied = true;
            }
        });
    }

    private findInvitation(routeName: string) {
        if (this.inviteCode) {
            const nextRoute: Location = {
                name: routeName
            };

            findInvitation(this.inviteCode)
                .then(this.initLocalState)
                .catch(this.apiError)
                .finally(() => {
                    // Add invite code only if the invite is valid.
                    if (this.isInviteCodeValid) {
                        nextRoute.query = {
                            inviteCode: this.inviteCode
                        };
                    }
                    this.$router.replace(nextRoute);
                });
        }
    }

    private initLocalState(response: UserData) {
        this.isInviteCodeValid = true;
        this.firstName = response.firstName;
        this.lastName = response.lastName;
        this.email = response.email;
        this.checkAvailability();
    }

    private apiError(response) {
        this.isInviteCodeValid = false;
        matchError({
            422: ({ body }) => this.$snackbar.error({ message: body.errors[0].message }),
            anyError: ({ body }) => this.$snackbar.error({
                message: 'This invite is no longer valid, however you can still continue with the registration.'
            })
        })(response);
    }
}
